import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Box, Divider, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';

import useNotification from '../../../../core/hooks/use_notification';
import { createDeliveryNotificationNotificationData, getDeliveryNotificationNotificationData, updateDeliveryNotificationNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { GET_ROUTE_ROUTE_DELIVERY_NOTIFICATIONS_EDIT, ROUTE_DELIVERY_NOTIFICATIONS } from '../../../../core/constants';
import TextFieldFormik from '../../../common/text_field';
import { SubmitButton } from '../../../common/button';
import { FormRouteEnum } from '../../../../core/enums/common/route';
import { getLanguagesState } from '../../../../features/product/get_languages';
import { getDeliveryNotificationAction, getDeliveryNotificationState } from '../../../../features/communication/get_delivery_notification';
import { createDeliveryNotificationAction, createDeliveryNotificationState } from '../../../../features/communication/create_delivery_notification';
import { updateDeliveryNotificationAction, updateDeliveryNotificationState } from '../../../../features/communication/update_delivery_notification';
import PageHeader from '../../../common/page_header';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from "../../../common/bottom_action_bar";

const DeliveryNotificationForm = () => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { deliveryNotificationId } = useParams();
    const location = useLocation();

    const getDeliveryNotification = useSelector(getDeliveryNotificationState);
    const createDeliveryNotification = useSelector(createDeliveryNotificationState);
    const updateDeliveryNotification = useSelector(updateDeliveryNotificationState);
    const getLanguages = useSelector(getLanguagesState);

    const createNewForm = location?.state?.action === FormRouteEnum.NEW;
    const deliveryNotificationIdToDuplicate = location?.state?.deliveryNotificationIdToDuplicate;

    const initialValues = {
        workingName: ((deliveryNotificationId || deliveryNotificationIdToDuplicate) && getDeliveryNotification?.data?.workingName) || '',
        translatedMessages: getLanguages?.data?.map(language => (
            {
                languageCode: language.languageCode,
                title: ((deliveryNotificationId || deliveryNotificationIdToDuplicate) && getDeliveryNotification?.data?.translatedMessages?.find(dn => dn.languageCode === language.languageCode)?.title) || '',
                body: ((deliveryNotificationId || deliveryNotificationIdToDuplicate) && getDeliveryNotification?.data?.translatedMessages?.find(dn => dn.languageCode === language.languageCode)?.body) || '',
            }
        ))
    };

    const schema = Yup.object().shape({
        workingName: Yup.string().required(),
        translatedMessages: Yup.array().of(
            Yup.object().shape(
                {
                    languageCode: Yup.string().required(),
                    title: Yup.string().required(),
                    body: Yup.string().required(),
                },
                ['title', 'body']
            )
        ),
    });

    useEffect(() => {
        const id = deliveryNotificationId || deliveryNotificationIdToDuplicate;

        if (id) {
            dispatch(getDeliveryNotificationAction(id))
                .then(response => showErrorMessage((getDeliveryNotificationNotificationData(response))));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryNotificationId, deliveryNotificationIdToDuplicate, dispatch]);

    const onSubmit = (values) => {
        const form = { ...values };

        if (createNewForm) {
            dispatch(createDeliveryNotificationAction(form))
                .then(response => {
                    showNotification(createDeliveryNotificationNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        navigate(GET_ROUTE_ROUTE_DELIVERY_NOTIFICATIONS_EDIT(response?.payload.id));
                    }
                });
        } else {
            dispatch(updateDeliveryNotificationAction({ form, id: deliveryNotificationId }))
                .then(response => {
                    showNotification(updateDeliveryNotificationNotificationData(response));
                });
        }
    };

    const renderView = () => {
        if (getDeliveryNotification?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        return (
            <Formik
                initialValues={ initialValues }
                validationSchema={ schema }
                validateOnChange={ false }
                validateOnBlur={ false }
                enableReinitialize
                onSubmit={ onSubmit }>
                { () => (
                    <Box component={ Form }
                         sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                        <Box sx={ { flexGrow: 1, pb: 4 } }>
                            <Stack spacing={ 3 }>

                                <SectionPaper title="Dane podstawowe">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } md={ 6 }>
                                            <TextFieldFormik name="workingName" label="Nazwa robocza" required/>
                                        </Grid>
                                    </Grid>
                                </SectionPaper>

                                <SectionPaper title="Wielojęzyczna treść powiadomienia">
                                    <FieldArray name="translatedMessages">
                                        { () => (
                                            getLanguages?.data?.map((language, index) => (
                                                <Box key={ index }
                                                     sx={ { mb: index !== getLanguages.data.length - 1 ? 4 : 0 } }>
                                                    <Typography variant="subtitle1" color="primary"
                                                                sx={ { fontWeight: 600, mb: 2 } }>
                                                        { language.languageName }
                                                    </Typography>

                                                    <Grid container spacing={ 2 }>
                                                        <Grid item xs={ 12 }>
                                                            <TextFieldFormik
                                                                name={ `translatedMessages.${ index }.title` }
                                                                label="Tytuł" required/>
                                                        </Grid>

                                                        <Grid item xs={ 12 }>
                                                            <TextFieldFormik
                                                                name={ `translatedMessages.${ index }.body` }
                                                                label="Treść" multiline rows={ 6 } required/>
                                                        </Grid>
                                                    </Grid>

                                                    { index !== getLanguages.data.length - 1 && (
                                                        <Divider sx={ { mt: 4 } }/>
                                                    ) }
                                                </Box>
                                            ))
                                        ) }
                                    </FieldArray>
                                </SectionPaper>

                            </Stack>
                        </Box>

                        <BottomActionBar>
                            <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                                <SubmitButton
                                    isLoading={ createDeliveryNotification?.loading || updateDeliveryNotification?.loading }>
                                    Zapisz wzór
                                </SubmitButton>
                            </Box>
                        </BottomActionBar>

                    </Box>
                ) }
            </Formik>
        );
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title={ createNewForm ? 'Nowy wzór powiadomienia' : 'Edycja wzoru powiadomienia' }
                showBack={ true }
                onBack={ () => navigate(ROUTE_DELIVERY_NOTIFICATIONS) }
                titleAdornment={
                    !createNewForm && deliveryNotificationId && getDeliveryNotification?.data && (
                        <Typography variant="h6" color="textSecondary">
                            ID: { deliveryNotificationId }
                        </Typography>
                    )
                }
            />

            { renderView() }
        </Box>
    );
};

export default DeliveryNotificationForm;